/**
 * VendorPayoutWizard — Shopify-style one-screen embedded Stripe Connect
 * onboarding. No redirect, no pre-wizard. The vendor lands here, we create
 * their Connect account on mount with safe defaults, and the embedded
 * `<ConnectAccountOnboarding/>` collects everything else natively — bank
 * account, SSN/EIN, identity doc, address — all inside EntrepreNatives.
 *
 * We also mount `<ConnectNotificationBanner/>` at the top so any Stripe
 * "action required" items (restrictive actions, tax forms) surface
 * immediately.
 *
 * NOTE: The previous 3-step wizard (business → contact → payouts) added
 * friction without saving the vendor from any Stripe field. The embedded
 * component already collects business name, address, phone, email, DOB,
 * tax ID, bank account, and identity document in one scroll. Dropping the
 * pre-wizard brings the experience to Shopify parity ("one click → tell
 * Stripe your details inside our app → done").
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, Lock, ShieldCheck } from 'lucide-react';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import {
  ConnectAccountOnboarding,
  ConnectComponentsProvider,
  ConnectNotificationBanner,
} from '@stripe/react-connect-js';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// ─────────────────────────────────────────────────────────────────────────
// Dark-glass appearance matched to EntrepreNatives aesthetic.
// ─────────────────────────────────────────────────────────────────────────
const CONNECT_APPEARANCE = {
  overlays: 'dialog',
  variables: {
    colorPrimary: '#8B5CF6',
    colorBackground: '#13153A',
    colorText: '#FFFFFF',
    colorSecondaryText: '#C4B5FD',
    colorDanger: '#EF4444',
    actionPrimaryColorText: '#FFFFFF',
    actionSecondaryColorText: '#C4B5FD',
    offsetBackgroundColor: '#1A1D4A',
    formBackgroundColor: '#1A1D4A',
    buttonPrimaryColorBackground: '#8B5CF6',
    buttonPrimaryColorText: '#FFFFFF',
    buttonSecondaryColorBackground: '#2D2D52',
    buttonSecondaryColorText: '#C4B5FD',
    buttonBorderRadius: '9999px',
    formHighlightColorBorder: '#8B5CF6',
    formAccentColor: '#8B5CF6',
    badgeNeutralColorBackground: '#2D2D52',
    badgeNeutralColorText: '#C4B5FD',
    badgeSuccessColorBackground: 'rgba(16,185,129,0.15)',
    badgeSuccessColorText: '#86EFAC',
    badgeWarningColorBackground: 'rgba(245,158,11,0.15)',
    badgeWarningColorText: '#FCD34D',
    badgeDangerColorBackground: 'rgba(239,68,68,0.15)',
    badgeDangerColorText: '#FCA5A5',
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSizeBase: '15px',
    borderRadius: '12px',
    spacingUnit: '9px',
  },
};

// ─────────────────────────────────────────────────────────────────────────
export default function VendorPayoutWizard() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [connectInstance, setConnectInstance] = useState(null);
  const [error, setError] = useState('');
  const [bootstrapping, setBootstrapping] = useState(true);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const bootedRef = useRef(false);

  // ── Bootstrap: create account (idempotent) + load the Connect instance ──
  const bootstrap = useCallback(async () => {
    try {
      // 1. Make sure the vendor has a Stripe Connect account. Idempotent —
      //    returns `existing: true` on subsequent calls.
      await axios.post(
        `${API}/vendor/payout-wizard/create-stripe-account`,
        { origin_url: window.location.origin },
        { headers: getAuthHeaders() },
      );

      // 2. Build the Connect instance. The `fetchClientSecret` callback
      //    requests a fresh AccountSession for every component mount.
      const instance = loadConnectAndInitialize({
        publishableKey: PUBLISHABLE_KEY,
        fetchClientSecret: async () => {
          const res = await axios.post(
            `${API}/vendor/payout-wizard/account-session`,
            {},
            { headers: getAuthHeaders() },
          );
          return res.data.client_secret;
        },
        appearance: CONNECT_APPEARANCE,
      });
      setConnectInstance(instance);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Could not start payout setup';
      setError(msg);
      toast.error(msg);
    } finally {
      setBootstrapping(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    bootstrap();
  }, [bootstrap]);

  const completeAndFinish = useCallback(async () => {
    setOnboardingComplete(true);
    try {
      await axios.get(`${API}/vendor/payout-wizard/status`, { headers: getAuthHeaders() });
    } catch {}
    toast.success('Payouts ready — you can start selling.');
    setTimeout(() => navigate('/dashboard/vendor/earnings'), 1200);
  }, [getAuthHeaders, navigate]);

  // ─────────────────────────────── RENDER ─────────────────────────────
  return (
    <div className="min-h-screen bg-[#06061A] text-white">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 py-10 lg:py-14" data-testid="payout-wizard">
        <div className="text-center mb-8">
          <p className="text-xs uppercase tracking-[0.22em] text-violet-300 font-bold">EntrepreNatives · Payouts</p>
          <h1
            className="text-3xl md:text-4xl font-bold mt-2"
            style={{ fontFamily: 'Syne, system-ui' }}
            data-testid="payout-wizard-title"
          >
            Set up payouts
          </h1>
          <p className="text-white/60 mt-3 max-w-xl mx-auto">
            Tell us about your business and connect a bank account to start getting paid. Takes ~3 minutes, no redirects.
          </p>
          <div className="mt-4 flex items-center justify-center gap-4 text-[11px] text-white/40">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} className="text-emerald-400" /> Bank-grade encryption</span>
            <span className="inline-flex items-center gap-1.5"><Lock size={12} className="text-emerald-400" /> We never see your bank digits</span>
          </div>
        </div>

        {/* ── Shell: single card holds the embedded onboarding + banner ── */}
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5 md:p-8" data-testid="payout-wizard-card">
          {onboardingComplete ? (
            <div className="text-center py-10" data-testid="payout-wizard-complete">
              <CheckCircle2 size={44} className="text-emerald-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Syne, system-ui' }}>You&apos;re all set</h2>
              <p className="text-white/60 mt-2 text-sm">Taking you to your earnings dashboard…</p>
            </div>
          ) : bootstrapping ? (
            <div className="flex flex-col items-center justify-center py-14 text-white/60">
              <Loader2 className="w-7 h-7 animate-spin text-[#8B5CF6] mb-3" />
              <p className="text-sm">Preparing your payout account…</p>
            </div>
          ) : error ? (
            <div className="py-10 text-center" data-testid="payout-wizard-error">
              <p className="text-sm text-red-300">We couldn&apos;t start your payouts: {error}</p>
              <button
                onClick={() => { bootedRef.current = false; setError(''); setBootstrapping(true); bootstrap(); }}
                className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider bg-white/10 border border-white/20 hover:bg-white/15"
                data-testid="payout-wizard-retry"
              >
                Try again
              </button>
            </div>
          ) : connectInstance ? (
            <ConnectComponentsProvider connectInstance={connectInstance}>
              {/* Surfaces Stripe "action required" alerts (tax forms, risk
                  review, restrictive actions) at the top. Styled to match
                  our dark theme via appearance variables above. */}
              <ConnectNotificationBanner />
              <div className="mt-4" data-testid="payout-wizard-onboarding">
                <ConnectAccountOnboarding onExit={completeAndFinish} />
              </div>
            </ConnectComponentsProvider>
          ) : null}
        </div>

        <p className="mt-6 text-center text-[11px] text-white/35">
          Zero platform fees · Payouts land in your bank in 2 business days (US) or instantly to a debit card.
        </p>
      </main>
    </div>
  );
}
